import React, { useState, useEffect } from 'react';
import { IonModal, IonButton, IonSpinner, IonIcon } from '@ionic/react';
import { warningOutline } from 'ionicons/icons';
import accountService, { DELETE_CONFIRM_TOKEN } from '../../../../services/account';
import AuthService from '../../../../services/auth';
import './SettingsModal.css';

/**
 * SettingsModal — the account "Settings" surface reached from the avatar
 * dropdown. Today it hosts a single, DANGEROUS section: "Delete account".
 *
 * Two-stage guard:
 *   Stage 1 — a clearly-styled Danger Zone explaining what permanent deletion
 *             means, with a single "Delete account" button.
 *   Stage 2 — a confirmation panel that requires the user to TYPE the literal
 *             confirmation token (`DELETE`) before the destructive button
 *             enables. On confirm → DELETE /account → clear session + logout.
 *
 * The backend independently enforces owner-only auth + the same confirm token,
 * so this is defense-in-depth UX, not the sole gate.
 */
const SettingsModal = ({ isOpen, onDismiss, user }) => {
  const [confirming, setConfirming] = useState(false); // stage 2 shown?
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset all transient state whenever the modal (re)opens.
  useEffect(() => {
    if (isOpen) {
      setConfirming(false);
      setTyped('');
      setSubmitting(false);
      setError('');
    }
  }, [isOpen]);

  const canConfirm = typed.trim() === DELETE_CONFIRM_TOKEN && !submitting;

  const handleDelete = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    setError('');
    try {
      await accountService.deleteAccount();
      // Success — wipe the local session and bounce to logout. AuthService
      // clears tokens + cross-domain cookies and redirects to /login.
      AuthService.logout();
    } catch (err) {
      setError(
        err?.body?.error || err?.message || 'Could not delete account. Please try again.'
      );
      setSubmitting(false);
    }
  };

  const dismiss = () => {
    if (submitting) return; // don't let a dismiss race the in-flight delete
    onDismiss();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={dismiss} className="settings-modal">
      <div className="settings-modal-content">
        <h2 className="settings-modal-title">Settings</h2>
        {user?.email && (
          <p className="settings-modal-subtitle">{user.email}</p>
        )}

        {/* ── Danger Zone ──────────────────────────────────────────────── */}
        <div className="settings-danger-zone">
          <div className="settings-danger-header">
            <IonIcon icon={warningOutline} className="settings-danger-icon" />
            <span>Danger zone</span>
          </div>

          <div className="settings-danger-body">
            <div className="settings-danger-text">
              <div className="settings-danger-heading">Delete account</div>
              <p className="settings-danger-desc">
                Permanently delete your account and <strong>all</strong> of its
                data — projects, workflows, runs, integrations, hosted apps,
                credentials and billing history. Your subscription is cancelled.
                This action <strong>cannot be undone</strong>.
              </p>
            </div>

            {!confirming ? (
              <IonButton
                color="danger"
                fill="outline"
                className="settings-danger-btn"
                onClick={() => { setConfirming(true); setError(''); }}
              >
                Delete account
              </IonButton>
            ) : (
              <div className="settings-confirm-block">
                <label className="settings-confirm-label">
                  Type <code>{DELETE_CONFIRM_TOKEN}</code> to confirm
                </label>
                <input
                  type="text"
                  className="settings-confirm-input"
                  value={typed}
                  placeholder={DELETE_CONFIRM_TOKEN}
                  onChange={(e) => { setTyped(e.target.value || ''); setError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && canConfirm) handleDelete(); }}
                  disabled={submitting}
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                />

                {error && <p className="settings-modal-error">{error}</p>}

                <div className="settings-confirm-buttons">
                  <IonButton
                    color="danger"
                    expand="block"
                    onClick={handleDelete}
                    disabled={!canConfirm}
                  >
                    {submitting
                      ? <IonSpinner name="crescent" />
                      : 'Permanently delete account'}
                  </IonButton>
                  <IonButton
                    expand="block"
                    fill="clear"
                    onClick={() => { setConfirming(false); setTyped(''); setError(''); }}
                    disabled={submitting}
                  >
                    Cancel
                  </IonButton>
                </div>
              </div>
            )}
          </div>
        </div>

        {!confirming && (
          <div className="settings-modal-footer">
            <IonButton expand="block" fill="clear" onClick={dismiss}>
              Close
            </IonButton>
          </div>
        )}
      </div>
    </IonModal>
  );
};

export default SettingsModal;
